import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { ActiveEditorContext } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { CHAT_CATEGORY, isChatViewTitleActionContext } from 'vscode/vscode/vs/workbench/contrib/chat/browser/actions/chatActions';
import { CHAT_VIEW_ID } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat';
import { IChatWidgetService } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat.service';
import { ChatEditorInput } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chatEditorInput';
import { CONTEXT_CHAT_ENABLED } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatContextKeys';
import { IEditorGroupsService } from 'vscode/vscode/vs/workbench/services/editor/common/editorGroupsService.service';
import { AUX_WINDOW_GROUP, ACTIVE_GROUP } from 'vscode/vscode/vs/workbench/services/editor/common/editorService';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';

const _moduleId = "vs/workbench/contrib/chat/browser/actions/chatMoveActions";
var MoveToNewLocation;
( ((function(MoveToNewLocation) {
    MoveToNewLocation["Editor"] = "Editor";
    MoveToNewLocation["Window"] = "Window";
})(MoveToNewLocation || (MoveToNewLocation = {}))));
function registerMoveActions() {
    registerAction2(class GlobalMoveToEditorAction extends Action2 {
        constructor() {
            super({
                id: `workbench.action.chat.openInEditor`,
                title: ( localize2WithPath(_moduleId, 0, "Open Chat in Editor")),
                category: CHAT_CATEGORY,
                precondition: CONTEXT_CHAT_ENABLED,
                f1: true,
                menu: {
                    id: MenuId.ViewTitle,
                    when: ( (ContextKeyExpr.equals('view', CHAT_VIEW_ID))),
                    order: 0
                },
            });
        }
        async run(accessor, ...args) {
            const context = args[0];
            executeMoveToAction(accessor, MoveToNewLocation.Editor, isChatViewTitleActionContext(context) ? context.chatView : undefined);
        }
    });
    registerAction2(class GlobalMoveToNewWindowAction extends Action2 {
        constructor() {
            super({
                id: `workbench.action.chat.openInNewWindow`,
                title: ( localize2WithPath(_moduleId, 1, "Open Chat in New Window")),
                category: CHAT_CATEGORY,
                precondition: CONTEXT_CHAT_ENABLED,
                f1: true,
                menu: {
                    id: MenuId.ViewTitle,
                    when: ( (ContextKeyExpr.equals('view', CHAT_VIEW_ID))),
                    order: 0
                },
            });
        }
        async run(accessor, ...args) {
            const context = args[0];
            executeMoveToAction(accessor, MoveToNewLocation.Window, isChatViewTitleActionContext(context) ? context.chatView : undefined);
        }
    });
    registerAction2(class GlobalMoveToSidebarAction extends Action2 {
        constructor() {
            super({
                id: `workbench.action.chat.openInSidebar`,
                title: ( localize2WithPath(_moduleId, 2, "Open Chat in Side Bar")),
                category: CHAT_CATEGORY,
                precondition: CONTEXT_CHAT_ENABLED,
                f1: true,
                menu: [{
                        id: MenuId.EditorTitle,
                        order: 0,
                        when: ( (ActiveEditorContext.isEqualTo(ChatEditorInput.EditorID))),
                    }]
            });
        }
        async run(accessor, ...args) {
            return moveToSidebar(accessor);
        }
    });
}
async function executeMoveToAction(accessor, moveTo, chatView) {
    const widgetService = accessor.get(IChatWidgetService);
    const viewService = accessor.get(IViewsService);
    const editorService = accessor.get(IEditorService);
    const widget = chatView?.widget ?? widgetService.lastFocusedWidget;
    if (!widget || !('viewId' in widget.viewContext)) {
        await editorService.openEditor({ resource: ChatEditorInput.getNewEditorUri(), options: { pinned: true } }, moveTo === MoveToNewLocation.Window ? AUX_WINDOW_GROUP : ACTIVE_GROUP);
        return;
    }
    const viewModel = widget.viewModel;
    if (!viewModel) {
        return;
    }
    const sessionId = viewModel.sessionId;
    const view = await viewService.openView(widget.viewContext.viewId);
    const viewState = view.widget.getViewState();
    view.clear();
    await editorService.openEditor({ resource: ChatEditorInput.getNewEditorUri(), options: { target: { sessionId }, pinned: true, viewState: viewState } }, moveTo === MoveToNewLocation.Window ? AUX_WINDOW_GROUP : ACTIVE_GROUP);
}
async function moveToSidebar(accessor) {
    const viewsService = accessor.get(IViewsService);
    const editorService = accessor.get(IEditorService);
    const editorGroupService = accessor.get(IEditorGroupsService);
    const chatEditorInput = editorService.activeEditor;
    if (chatEditorInput instanceof ChatEditorInput && chatEditorInput.sessionId) {
        await editorService.closeEditor({ editor: chatEditorInput, groupId: editorGroupService.activeGroup.id });
        const view = await viewsService.openView(CHAT_VIEW_ID);
        view.loadSession(chatEditorInput.sessionId);
    }
    else {
        await viewsService.openView(CHAT_VIEW_ID);
    }
}

export { registerMoveActions };
